import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { FIELD_TYPES } from '../utils/fieldTypes';

const CONDITION_OPERATORS = [
  { value: 'equals', label: 'Es igual a' },
  { value: 'not_equals', label: 'No es igual a' },
  { value: 'contains', label: 'Contiene' },
  { value: 'not_empty', label: 'No está vacío' },
  { value: 'is_empty', label: 'Está vacío' },
];

// Campos del formulario base que pueden usarse como referencia en condiciones
const BASE_FIELDS_FOR_CONDITIONS = [
  { id: 'sex', label: 'Sexo' },
  { id: 'country', label: 'País' },
  { id: 'birthYear', label: 'Año de nacimiento' },
  { id: 'docType', label: 'Tipo de documento' },
  { id: 'autorizaImagenRedes', label: 'Autoriza imagen redes' },
  { id: 'autorizaComunicaciones', label: 'Autoriza comunicaciones' },
];

const emptyField = () => ({
  id: `field_${Date.now()}`,
  label: '', type: 'text', required: false, scope: 'member', placeholder: '', options: [],
  condition: null,
});

// Claves estables para excluir del formulario (persistidas en event.excludedMandatory).
// Tipos de clave: "section:<nombre>" para quitar toda una sección, "field:<id>" para quitar un campo concreto.
const MANDATORY_FIELDS = [
  { section: 'Datos personales', fields: [
    { label: 'Nombre', type: 'text' },
    { label: 'Primer apellido', type: 'text' },
    { label: 'Segundo apellido', type: 'text', excludable: 'field:Segundo apellido' },
    { label: 'Año de nacimiento', type: 'number' },
    { label: 'Sexo', type: 'select', excludable: 'field:Sexo' },
  ]},
  { section: 'Contacto', fields: [
    { label: 'Email', type: 'email', excludable: 'field:Email' },
    { label: 'Teléfono', type: 'phone', excludable: 'field:Teléfono' },
    { label: 'Nombre tutor legal (menores)', type: 'text', excludable: 'field:Nombre tutor legal' },
    { label: 'Documento tutor (menores)', type: 'text', excludable: 'field:Documento tutor' },
  ]},
  { section: 'Domicilio', excludable: 'section:Domicilio', fields: [
    { label: 'Dirección', type: 'text', excludable: 'field:Dirección' },
    { label: 'Municipio', type: 'text', excludable: 'field:Municipio' },
    { label: 'Provincia', type: 'text', excludable: 'field:Provincia' },
    { label: 'País', type: 'select', excludable: 'field:País' },
  ]},
  { section: 'Documento de identidad (mayores 16)', excludable: 'section:Documento de identidad', fields: [
    { label: 'Tipo de documento', type: 'select', excludable: 'field:Tipo de documento' },
    { label: 'Número', type: 'text', excludable: 'field:Número documento' },
    { label: 'Soporte (dorso)', type: 'text', excludable: 'field:Soporte documento' },
    { label: 'Fecha emisión', type: 'date', excludable: 'field:Fecha emisión documento' },
  ]},
  { section: 'Información de salud', excludable: 'section:Información de salud', fields: [
    { label: 'Alergias', type: 'textarea', excludable: 'field:Alergias' },
    { label: 'Medicación actual', type: 'textarea', excludable: 'field:Medicación actual' },
    { label: 'Enfermedades / condiciones', type: 'textarea', excludable: 'field:Enfermedades' },
    { label: 'Grupo sanguíneo', type: 'select', excludable: 'field:Grupo sanguíneo' },
    { label: 'Nº tarjeta sanitaria', type: 'text', excludable: 'field:Tarjeta sanitaria' },
  ]},
  { section: 'Contacto de emergencia', excludable: 'section:Contacto de emergencia', fields: [
    { label: 'Nombre completo', type: 'text', excludable: 'field:Nombre emergencia' },
    { label: 'Teléfono', type: 'phone', excludable: 'field:Teléfono emergencia' },
    { label: 'Relación', type: 'select', excludable: 'field:Relación emergencia' },
  ]},
  { section: 'Autorizaciones', fields: [
    { label: 'Autoriza medicación', type: 'boolean', excludable: 'field:Autoriza medicación' },
    { label: 'Cesión de datos personales (RGPD)', type: 'boolean', note: 'Obligatorio' },
    { label: 'Uso de imagen en redes sociales', type: 'boolean', excludable: 'field:Uso de imagen en redes sociales' },
    { label: 'Comunicaciones comerciales', type: 'boolean', excludable: 'field:Comunicaciones comerciales' },
  ]},
];

export default function FormBuilder({ schema = [], onChange, registrationType = 'group', excludedMandatory = [], onExcludedMandatoryChange }) {
  const isExcluded = (key) => excludedMandatory.includes(key);
  const toggleExcluded = (key) => {
    if (!onExcludedMandatoryChange) return;
    const next = isExcluded(key)
      ? excludedMandatory.filter((k) => k !== key)
      : [...excludedMandatory, key];
    onExcludedMandatoryChange(next);
  };
  const { token } = useAuth();
  const [adding, setAdding] = useState(false);
  const [newField, setNewField] = useState(emptyField());
  const [optionInput, setOptionInput] = useState('');
  const [packs, setPacks] = useState([]);
  const [loadingPacks, setLoadingPacks] = useState(false);
  const [showPacks, setShowPacks] = useState(false);
  const [expandedPack, setExpandedPack] = useState(null);
  // selectedFields: { [packId]: Set of field ids to include }
  const [selectedFields, setSelectedFields] = useState({});

  useEffect(() => {
    if (!showPacks || packs.length > 0) return;
    setLoadingPacks(true);
    api.request('GET', '/api/admin/question-packs', null, token)
      .then(d => setPacks(d.packs || []))
      .catch(() => {})
      .finally(() => setLoadingPacks(false));
  }, [showPacks, token]);

  const add = () => {
    if (!newField.label.trim()) return;
    onChange([...schema, { ...newField, id: `field_${Date.now()}` }]);
    setNewField(emptyField());
    setOptionInput('');
    setAdding(false);
  };

  const remove = (id) => onChange(schema.filter(f => f.id !== id));
  const move = (i, dir) => {
    const arr = [...schema];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };

  // Inicializar selección de un paquete (todos seleccionados por defecto)
  const openPack = (pack) => {
    setExpandedPack(expandedPack === pack.id ? null : pack.id);
    if (!selectedFields[pack.id]) {
      setSelectedFields(p => ({ ...p, [pack.id]: new Set(pack.fields.map(f => f.id)) }));
    }
  };

  const toggleField = (packId, fieldId) => {
    setSelectedFields(p => {
      const set = new Set(p[packId] || []);
      if (set.has(fieldId)) set.delete(fieldId);
      else set.add(fieldId);
      return { ...p, [packId]: set };
    });
  };

  const addPackFields = (pack) => {
    const sel = selectedFields[pack.id] || new Set(pack.fields.map(f => f.id));
    const toAdd = pack.fields
      .filter(f => sel.has(f.id))
      .map(f => ({ ...f, id: `${f.id}_${Date.now()}`, fromPack: pack.id, packName: pack.name }));
    if (toAdd.length === 0) return;
    onChange([...schema, ...toAdd]);
    setExpandedPack(null);
    setShowPacks(false);
  };

  const familyFields = schema.filter(f => f.scope === 'family');
  const memberFields = schema.filter(f => f.scope === 'member' || !f.scope);

  const categories = [...new Set(packs.map(p => p.category).filter(Boolean))];
  const [filterCat, setFilterCat] = useState('');
  const filteredPacks = filterCat ? packs.filter(p => p.category === filterCat) : packs;

  const FieldList = ({ fields, scope }) => (
    <div style={{ background: 'rgba(255,255,255,.03)', border: '1px solid rgba(122,171,138,.15)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: '.8rem' }}>
      <div style={{ padding: '.72rem 1rem', borderBottom: '1px solid rgba(122,171,138,.12)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(122,171,138,.08)' }}>
        <div style={{ fontSize: '.62rem', fontWeight: 600, letterSpacing: '.12em', color: 'var(--sage)' }}>
          {scope === 'family' ? '◈ Datos del grupo (se rellenan una vez)' : '⚔ Datos por persona'}
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => { setNewField({ ...emptyField(), scope }); setAdding(true); setShowPacks(false); }}>+ Campo manual</button>
      </div>
      {fields.length === 0 ? (
        <div style={{ padding: '1rem', fontSize: '.85rem', color: 'var(--mid)', fontStyle: 'italic', textAlign: 'center' }}>Sin campos personalizados</div>
      ) : (
        fields.map((f, i) => {
          const realIdx = schema.findIndex(x => x.id === f.id);
          return (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '.8rem', padding: '.62rem 1rem', borderBottom: '1px solid rgba(122,171,138,.07)' }}>
              <div style={{ width: 26, height: 26, background: 'rgba(122,171,138,.1)', border: '1px solid rgba(122,171,138,.2)', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.75rem', color: 'var(--sage)', flexShrink: 0 }}>
                {{ text:'✎', textarea:'≡', number:'#', date:'📅', select:'▾', boolean:'◉', email:'@', phone:'📱', file:'📎' }[f.type]||'✎'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '.88rem', fontWeight: 600 }}>{f.label}</div>
                {f.condition && f.condition.fieldId && (
                  <div style={{ fontSize: '.65rem', color: 'var(--amber)', marginTop: '.1rem' }}>
                    ⚡ Condicional: {f.condition.fieldId} {CONDITION_OPERATORS.find(o => o.value === f.condition.operator)?.label || f.condition.operator} {f.condition.value || ''}
                  </div>
                )}
              </div>
              <div style={{ fontSize: '.7rem', color: 'var(--mid)', fontWeight: 500 }}>
                {FIELD_TYPES.find(t => t.value === f.type)?.label} {f.required ? '· Obligatorio' : ''}
              </div>
              <div style={{ display: 'flex', gap: '.25rem' }}>
                <button className="btn btn-ghost btn-sm" style={{ padding: '.2rem .4rem' }} onClick={() => move(realIdx, -1)}>↑</button>
                <button className="btn btn-ghost btn-sm" style={{ padding: '.2rem .4rem' }} onClick={() => move(realIdx, 1)}>↓</button>
                <button className="btn btn-danger btn-sm" style={{ padding: '.2rem .4rem' }} onClick={() => remove(f.id)}>✕</button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );

  return (
    <div>
      {/* Campos obligatorios (no eliminables) */}
      <div style={{ background: 'rgba(255,255,255,.03)', border: '1px solid rgba(122,171,138,.15)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: '1rem' }}>
        <div style={{ padding: '.72rem 1rem', borderBottom: '1px solid rgba(122,171,138,.12)', background: 'rgba(122,171,138,.08)' }}>
          <div style={{ fontSize: '.62rem', fontWeight: 600, letterSpacing: '.12em', color: 'var(--sage)', textTransform: 'uppercase' }}>
            🔒 Campos obligatorios (incluidos automáticamente)
          </div>
          <div style={{ fontSize: '.72rem', color: 'var(--mid)', marginTop: '.15rem' }}>
            Estos campos aparecen siempre en el formulario y no se pueden eliminar
          </div>
        </div>
        {MANDATORY_FIELDS.map(section => {
          const sectionExcluded = section.excludable && isExcluded(section.excludable);
          return (
            <div key={section.section}>
              <div style={{ padding: '.4rem 1rem', fontSize: '.68rem', fontWeight: 600, color: 'var(--sage)', borderBottom: '1px solid rgba(122,171,138,.05)', background: 'rgba(122,171,138,.04)', display: 'flex', alignItems: 'center', gap: '.5rem', justifyContent: 'space-between' }}>
                <span style={{ textDecoration: sectionExcluded ? 'line-through' : 'none', opacity: sectionExcluded ? .5 : 1 }}>{section.section}</span>
                {section.excludable && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    style={{ padding: '.15rem .5rem', fontSize: '.62rem', color: sectionExcluded ? 'var(--sage)' : 'var(--amber)' }}
                    onClick={() => toggleExcluded(section.excludable)}
                    title={sectionExcluded ? 'Reactivar esta sección' : 'Desbloquear y quitar esta sección del formulario'}
                  >
                    {sectionExcluded ? '↺ Reactivar' : '🔓 Quitar sección'}
                  </button>
                )}
              </div>
              {section.fields.map(f => {
                const fieldExcluded = sectionExcluded || (f.excludable && isExcluded(f.excludable));
                return (
                  <div key={f.label} style={{ display: 'flex', alignItems: 'center', gap: '.8rem', padding: '.45rem 1rem', borderBottom: '1px solid rgba(122,171,138,.04)', opacity: fieldExcluded ? .4 : .7, textDecoration: fieldExcluded ? 'line-through' : 'none' }}>
                    <div style={{ width: 22, height: 22, background: 'rgba(122,171,138,.08)', border: '1px solid rgba(122,171,138,.15)', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.7rem', color: 'var(--sage)', flexShrink: 0 }}>
                      {{ text:'✎', textarea:'≡', number:'#', date:'📅', select:'▾', boolean:'◉', email:'@', phone:'📱' }[f.type]||'✎'}
                    </div>
                    <span style={{ fontSize: '.82rem', flex: 1, color: 'var(--mid)' }}>{f.label}</span>
                    {f.excludable && !sectionExcluded ? (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={{ padding: '.1rem .4rem', fontSize: '.6rem', color: fieldExcluded ? 'var(--sage)' : 'var(--amber)' }}
                        onClick={() => toggleExcluded(f.excludable)}
                        title={fieldExcluded ? 'Reactivar este campo' : 'Quitar este campo del formulario'}
                      >
                        {fieldExcluded ? '↺ Reactivar' : '🔓 Quitar'}
                      </button>
                    ) : !section.excludable && !f.excludable ? (
                      <span style={{ fontSize: '.62rem', color: 'var(--sage)', fontWeight: 600 }}>🔒 Obligatorio</span>
                    ) : (
                      <span style={{ fontSize: '.62rem', color: 'var(--mid)' }}>—</span>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Botón añadir desde paquete */}
      <div style={{ display: 'flex', gap: '.5rem', marginBottom: '1rem' }}>
        <button className="btn btn-secondary btn-sm"
          onClick={() => { setShowPacks(p => !p); setAdding(false); }}>
          📦 {showPacks ? 'Ocultar paquetes' : 'Añadir desde paquete'}
        </button>
        {schema.length > 0 && (
          <span style={{ fontSize: '.75rem', color: 'var(--mid)', alignSelf: 'center' }}>
            {schema.length} campo{schema.length !== 1 ? 's' : ''} en el formulario
          </span>
        )}
      </div>

      {/* Panel de paquetes */}
      {showPacks && (
        <div style={{ background: 'rgba(255,255,255,.03)', border: '1px solid rgba(122,171,138,.15)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: '1rem' }}>
          <div style={{ padding: '.7rem 1rem', background: 'rgba(122,171,138,.08)', borderBottom: '1px solid rgba(122,171,138,.12)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '.65rem', fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--sage)' }}>
              📦 Paquetes disponibles
            </span>
            {categories.length > 0 && (
              <div style={{ display: 'flex', gap: '.3rem' }}>
                <button className={`btn btn-sm ${!filterCat ? 'btn-primary' : 'btn-ghost'}`}
                  style={{ padding: '.2rem .55rem', fontSize: '.68rem' }}
                  onClick={() => setFilterCat('')}>Todos</button>
                {categories.map(c => (
                  <button key={c} className={`btn btn-sm ${filterCat === c ? 'btn-primary' : 'btn-ghost'}`}
                    style={{ padding: '.2rem .55rem', fontSize: '.68rem' }}
                    onClick={() => setFilterCat(c)}>{c}</button>
                ))}
              </div>
            )}
          </div>

          {loadingPacks ? (
            <div style={{ padding: '1.5rem', textAlign: 'center' }}><span className="spinner" /></div>
          ) : filteredPacks.length === 0 ? (
            <div style={{ padding: '1rem', color: 'var(--mid)', fontStyle: 'italic', fontSize: '.85rem', textAlign: 'center' }}>
              No hay paquetes. Créalos en "Preguntas formularios".
            </div>
          ) : filteredPacks.map(pack => {
            const sel = selectedFields[pack.id] || new Set(pack.fields.map(f => f.id));
            const selCount = [...sel].filter(id => pack.fields.some(f => f.id === id)).length;
            const isExpanded = expandedPack === pack.id;
            return (
              <div key={pack.id} style={{ borderBottom: '1px solid rgba(122,171,138,.07)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '.7rem', padding: '.7rem 1rem' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, fontSize: '.88rem' }}>{pack.name}</span>
                      {pack.category && <span className="badge badge-moss" style={{ fontSize: '.6rem' }}>{pack.category}</span>}
                      <span style={{ fontSize: '.72rem', color: 'var(--mid)' }}>{pack.fields?.length || 0} campos</span>
                    </div>
                    {pack.description && <div style={{ fontSize: '.75rem', color: 'var(--mid)', marginTop: '.1rem' }}>{pack.description}</div>}
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => openPack(pack)}>
                    {isExpanded ? '▲ Ocultar' : '▼ Personalizar'}
                  </button>
                  <button className="btn btn-primary btn-sm"
                    onClick={() => { if (!isExpanded) setSelectedFields(p => ({ ...p, [pack.id]: new Set(pack.fields.map(f => f.id)) })); addPackFields(pack); }}>
                    + Añadir{selCount < (pack.fields?.length || 0) ? ` (${selCount})` : ' todo'}
                  </button>
                </div>

                {/* Campos del paquete expandidos para selección */}
                {isExpanded && (
                  <div style={{ padding: '.5rem 1rem .8rem', background: 'rgba(122,171,138,.04)' }}>
                    <div style={{ fontSize: '.72rem', color: 'var(--sage)', marginBottom: '.5rem', fontWeight: 600 }}>
                      Selecciona los campos que quieres incluir ({selCount}/{pack.fields?.length || 0}):
                    </div>
                    {pack.fields?.map(f => {
                      const checked = sel.has(f.id);
                      return (
                        <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '.6rem', padding: '.4rem .5rem', borderRadius: 6, cursor: 'pointer', background: checked ? 'rgba(122,171,138,.08)' : 'transparent', marginBottom: '.2rem' }}>
                          <input type="checkbox" checked={checked}
                            onChange={() => toggleField(pack.id, f.id)}
                            style={{ accentColor: 'var(--fern)', width: 14, height: 14 }} />
                          <span style={{ fontSize: '.78rem', color: 'var(--sage)', width: 16, textAlign: 'center' }}>
                            {{ text:'✎', textarea:'≡', number:'#', date:'📅', select:'▾', boolean:'◉', email:'@', phone:'📱' }[f.type]||'✎'}
                          </span>
                          <span style={{ fontSize: '.85rem', flex: 1, color: checked ? 'var(--white)' : 'var(--mid)' }}>{f.label}</span>
                          <span style={{ fontSize: '.68rem', color: 'var(--mid)' }}>
                            {FIELD_TYPES.find(t => t.value === f.type)?.label}
                            {f.required && ' · Obligatorio'}
                          </span>
                        </label>
                      );
                    })}
                    <div style={{ marginTop: '.6rem', display: 'flex', gap: '.4rem', justifyContent: 'flex-end' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setExpandedPack(null)}>Cancelar</button>
                      <button className="btn btn-primary btn-sm"
                        onClick={() => addPackFields(pack)}
                        disabled={selCount === 0}>
                        Añadir {selCount} campo{selCount !== 1 ? 's' : ''} seleccionado{selCount !== 1 ? 's' : ''}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {registrationType === 'group' && <FieldList fields={familyFields} scope="family" />}
      <FieldList fields={memberFields} scope="member" />

      {adding && (
        <div style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(122,171,138,.15)', borderRadius: 'var(--radius)', padding: '1rem', marginTop: '.5rem' }}>
          <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--sage)', marginBottom: '.8rem', letterSpacing: '.1em', textTransform: 'uppercase' }}>Nuevo campo manual</div>
          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label">Nombre del campo <span className="req">*</span></label>
              <input className="form-input" value={newField.label} onChange={e => setNewField(p => ({ ...p, label: e.target.value }))} placeholder="Ej: Nivel de experiencia" />
            </div>
            <div className="form-group">
              <label className="form-label">Tipo</label>
              <select className="form-select" value={newField.type} onChange={e => setNewField(p => ({ ...p, type: e.target.value }))}>
                {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
          {registrationType === 'group' && (
            <div className="form-group">
              <label className="form-label">Scope</label>
              <select className="form-select" value={newField.scope} onChange={e => setNewField(p => ({ ...p, scope: e.target.value }))}>
                <option value="member">Por persona</option>
                <option value="family">De grupo</option>
              </select>
            </div>
          )}
          {newField.type === 'select' && (
            <div className="form-group">
              <label className="form-label">Opciones (una por línea)</label>
              <textarea className="form-textarea" style={{ minHeight: 60 }} value={optionInput}
                onChange={e => { setOptionInput(e.target.value); setNewField(p => ({ ...p, options: e.target.value.split('\n').filter(Boolean) })); }}
                placeholder="Opción 1&#10;Opción 2" />
            </div>
          )}
          <div className="form-group" style={{ marginBottom: '.4rem' }}>
            <label className="toggle-wrap">
              <input type="checkbox" checked={newField.required} onChange={e => setNewField(p => ({ ...p, required: e.target.checked }))} />
              <span className="toggle-track"><span className="toggle-thumb" /></span>
              <span style={{ fontSize: '.88rem' }}>Campo obligatorio</span>
            </label>
          </div>

          {/* ── Condición (opcional) ── */}
          <div style={{ marginTop: '.6rem', padding: '.7rem .9rem', background: 'rgba(232,164,48,.06)', border: '1px solid rgba(232,164,48,.15)', borderRadius: 'var(--radius)' }}>
            <label className="toggle-wrap" style={{ marginBottom: '.5rem' }}>
              <input type="checkbox" checked={!!newField.condition}
                onChange={e => setNewField(p => ({ ...p, condition: e.target.checked ? { fieldId: '', operator: 'equals', value: '' } : null }))} />
              <span className="toggle-track"><span className="toggle-thumb" /></span>
              <span style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--amber)' }}>⚡ Mostrar solo si se cumple una condición</span>
            </label>
            {newField.condition && (
              <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', marginTop: '.4rem' }}>
                <select className="form-select" style={{ flex: '1 1 140px', fontSize: '.82rem' }}
                  value={newField.condition.fieldId}
                  onChange={e => setNewField(p => ({ ...p, condition: { ...p.condition, fieldId: e.target.value } }))}>
                  <option value="">— Campo referencia —</option>
                  <optgroup label="Campos del formulario base">
                    {BASE_FIELDS_FOR_CONDITIONS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                  </optgroup>
                  {schema.length > 0 && (
                    <optgroup label="Campos personalizados">
                      {schema.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                    </optgroup>
                  )}
                </select>
                <select className="form-select" style={{ flex: '0 1 130px', fontSize: '.82rem' }}
                  value={newField.condition.operator}
                  onChange={e => setNewField(p => ({ ...p, condition: { ...p.condition, operator: e.target.value } }))}>
                  {CONDITION_OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {!['not_empty', 'is_empty'].includes(newField.condition.operator) && (
                  <input className="form-input" style={{ flex: '1 1 120px', fontSize: '.82rem' }}
                    value={newField.condition.value || ''}
                    onChange={e => setNewField(p => ({ ...p, condition: { ...p.condition, value: e.target.value } }))}
                    placeholder="Valor" />
                )}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '.5rem', marginTop: '.8rem' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setAdding(false)}>Cancelar</button>
            <button className="btn btn-primary btn-sm" onClick={add} disabled={!newField.label.trim()}>Añadir campo</button>
          </div>
        </div>
      )}
    </div>
  );
}
